/**
 * GameCanvas — Phase 38 canvas foundation + Phase 39 activation/exit.
 *
 * Phase 38: full-viewport inert canvas with DPR sizing, RAF loop gated by isActive,
 *           StrictMode double-mount guard, touch device short-circuit.
 *
 * Phase 39: Escape handler opens canvas-rendered exit dialog (paused state freezes
 *           game loop updates). Arrow keys navigate [keep playing] / [exit]. Death
 *           dialog (state.dead) shows [try again] / [exit]. All exit paths converge
 *           on internal exitGame() which is registered with GameContext so the DOM
 *           [stop game] button in Home.tsx can call the identical teardown. The
 *           800ms opacity fade is driven inside the render callback via direct
 *           style.opacity mutation (no React state — avoids re-renders mid-fade).
 */

import React, { useEffect, useRef } from 'react';
import { startGameLoop } from '../office/gameLoop';
import { useGameContext } from './GameContext';
import type { GameState, GameTier, IncomingIndicator } from './types';
import {
  ENEMY_FONT,
  PILL_PAD_X,
  spawnEnemyAt,
  updateEnemy,
  drawEnemy,
  drawIncomingIndicator,
  getCharXOffset,
  measurePill,
  getSpawnPosition,
} from './enemies';
import { spawnParticle, updateParticles, drawParticles } from './particles';
import { extractWordPool, activateWord, deactivateWord, restoreAll } from './wordPool';
import { playSound, setMuted, isMuted, ensureAudioUnlocked } from './audio';
import { queueLine, updateDialogue, drawDialogue, advanceDialogue, isDialogueActive, resetDialogue, setSafeZoneLevel } from './dialogue';
import { initCharacter, updateCharacter, drawCharacter, setCharacterState, getCharacterState } from './character';
import {
  INTRO_LINES,
  PLAYER_HIT_POOL,
  TIER_LINES,
  EXIT_LINES,
  DEATH_POOL,
  PAUSE_QUIPS,
  LONG_SURVIVAL_LINE,
  getWordClearedQuip,
} from './dialogueScript';
import { tickQuips, onWaveClear } from './quips';
import { drawSafeZone, tickSafeZone, checkSafeZoneEntry, resetSafeZone } from './safeZone';
import { drawSelectionOverlay, tickSelectionWalls, checkSelectionDiscovery, isInsideSelection, handleSelectionChange, resetSelectionWalls, cleanupSelectionWalls } from './selectionWalls';
import {
  spawnStreakWarning,
  tickStreakWarnings,
  updateStreak,
  isStreakOffCanvas,
  checkTrailCollision,
  drawAllStreakWarnings,
  drawStreakTrail,
  drawStreakEnemy,
  resetStreak,
  getPendingWarningCount,
} from './streak';
import {
  spawnBomb,
  updateBomb,
  getBombBlastRadius,
  triggerBombDetonation,
  tickBlastFlash,
  drawBombEnemy,
  drawBlastFlash,
  resetBomb,
  BOMB_FUSE,
} from './bomb';

import type { LeaderboardEntry } from './types';

const TOUCH_MEDIA_QUERY = '(hover: none) and (pointer: coarse)';
const EXIT_FADE_MS = 800;
const MAX_LIVES = 3; // WR-02: single source of truth for starting lives
const LEADERBOARD_API = `${process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001'}/api/game/leaderboard`;
const LEADERBOARD_MAX_NAME = 10;

const CANVAS_BASE_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
  pointerEvents: 'none',
};

function createInitialState(): GameState {
  return {
    // Default to viewport center so enemies home correctly before first mousemove
    mouseX: window.innerWidth / 2,
    mouseY: window.innerHeight / 2,
    paused: false,
    dead: false,
    exiting: false,
    exitStartTime: 0,
    exitDialogSelected: 0,
    deathDialogSelected: 0,
    hasPlayedIntro: false,
    hasPlayedSurvival: false,
    // Phase 41 fields (fully wired in 41-03)
    wordPool: [],
    enemies: [],
    particles: [],
    wave: 1,
    wordsCleared: 0,
    score: 0,
    incomingFrames: 0,
    // Phase 42 — HUD/lives/wave announcement
    lives: MAX_LIVES,
    waveAnnouncement: null,
    incomingIndicators: [],
    lastAnnouncedWave: 1,  // suppress wave-1 opening announcement
    // Phase 47 — character personality system
    idleTimer: 0,
    movingTimer: 0,
    lastTypedFrame: 0,
    lastMouseMoveFrame: 0,
    quipCooldowns: new Map(),
    lastTierForQuip: 0,
    // Phase 48 — safe zone clear-all
    clearAllPhase: 'idle',
    clearAllTimer: 0,
    safeZoneUses: 0,
    safeZoneEntered: false,
    // Phase 49 — text selection walls
    selectionRects: [],
    selectionDirty: false,
    hasDiscoveredSelection: false,
    // Phase 50 — streak enemy
    streakSpawnTimer: 0,
    trailHitCooldown: 0,
    // Phase 51 — bomb enemy
    bombSpawnTimer: 0,
    // Leaderboard
    leaderboard: [],
    leaderboardLoading: false,
    leaderboardName: '',
    leaderboardQualifies: false,
    leaderboardSubmitted: false,
    leaderboardCursorBlink: 0,
  };
}


function drawExitDialog(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  selectedIndex: number,
  title: string,
  options: readonly string[]
): void {
  const W = 400;
  const H = 56 + options.length * 32 + 24;
  const x = (cssW - W) / 2;
  const y = (cssH - H) / 2;

  ctx.fillStyle = 'rgba(10, 10, 10, 0.92)';
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, W, H, 12);
  } else {
    ctx.rect(x, y, W, H);
  }
  ctx.fill();

  ctx.strokeStyle = '#555';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = '#ccc';
  ctx.font = '14px Menlo, monospace';
  ctx.textAlign = 'center';
  ctx.fillText(title, cssW / 2, y + 36);

  options.forEach((label, i) => {
    ctx.fillStyle = i === selectedIndex ? '#c084fc' : '#888';
    ctx.fillText(label, cssW / 2, y + 64 + i * 32);
  });
}

// ── Leaderboard helpers ────────────────────────────────────
async function fetchLeaderboard(): Promise<LeaderboardEntry[]> {
  try {
    const res = await fetch(LEADERBOARD_API);
    const data = await res.json();
    if (data.success) return data.data;
  } catch { /* silent */ }
  return [];
}

async function submitScore(
  name: string, score: number, wave: number, wordsCleared: number
): Promise<LeaderboardEntry[]> {
  try {
    const res = await fetch(LEADERBOARD_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, score, wave, wordsCleared }),
    });
    const data = await res.json();
    if (data.success && data.data.leaderboard) return data.data.leaderboard;
    if (data.success) return fetchLeaderboard();
  } catch { /* silent */ }
  return [];
}

function scoreQualifies(score: number, board: LeaderboardEntry[]): boolean {
  if (board.length < 5) return score > 0;
  return score > board[board.length - 1].score;
}

// Save/load personal best from localStorage
function getPersonalBest(): number {
  try { return parseInt(localStorage.getItem('cnf_game_pb') || '0', 10); } catch { return 0; }
}
function setPersonalBest(score: number): void {
  try { localStorage.setItem('cnf_game_pb', String(score)); } catch { /* silent */ }
}

// ── Death dialog with leaderboard ──────────────────────────
function drawDeathDialog(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  state: GameState,
): void {
  const W = 420;
  const boardRows = Math.max(state.leaderboard.length, 1);
  // Height: title + score line + leaderboard header + rows + name input (if qualifying) + options
  const hasNameInput = state.leaderboardQualifies && !state.leaderboardSubmitted;
  const nameInputH = hasNameInput ? 48 : 0;
  const H = 56 + 28 + 24 + boardRows * 24 + nameInputH + 16 + (hasNameInput ? 0 : 2 * 32) + 24;
  const x = (cssW - W) / 2;
  const y = (cssH - H) / 2;

  // Background
  ctx.fillStyle = 'rgba(10, 10, 10, 0.94)';
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, W, H, 12);
  else ctx.rect(x, y, W, H);
  ctx.fill();
  ctx.strokeStyle = '#555';
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.font = '14px Menlo, monospace';
  ctx.textAlign = 'center';

  // Title
  ctx.fillStyle = '#ccc';
  ctx.fillText('you died', cssW / 2, y + 32);

  // Score + personal best
  const pb = getPersonalBest();
  const isNewPb = state.score > pb;
  ctx.fillStyle = isNewPb ? '#c084fc' : '#888';
  ctx.fillText(`score: ${state.score}  |  wave: ${state.wave}${isNewPb ? '  |  new personal best!' : `  |  best: ${pb}`}`, cssW / 2, y + 56);

  // Leaderboard header
  let rowY = y + 84;
  ctx.fillStyle = '#c084fc';
  ctx.font = '12px Menlo, monospace';
  ctx.textAlign = 'left';
  ctx.fillText('RANK', x + 24, rowY);
  ctx.fillText('NAME', x + 80, rowY);
  ctx.textAlign = 'right';
  ctx.fillText('SCORE', x + W - 80, rowY);
  ctx.fillText('WAVE', x + W - 24, rowY);
  rowY += 4;

  // Divider line
  ctx.strokeStyle = '#444';
  ctx.beginPath();
  ctx.moveTo(x + 16, rowY);
  ctx.lineTo(x + W - 16, rowY);
  ctx.stroke();
  rowY += 16;

  if (state.leaderboardLoading) {
    ctx.fillStyle = '#666';
    ctx.textAlign = 'center';
    ctx.fillText('loading...', cssW / 2, rowY);
    rowY += 24;
  } else if (state.leaderboard.length === 0) {
    ctx.fillStyle = '#666';
    ctx.textAlign = 'center';
    ctx.fillText('no scores yet - be the first!', cssW / 2, rowY);
    rowY += 24;
  } else {
    for (let i = 0; i < state.leaderboard.length; i++) {
      const entry = state.leaderboard[i];
      const isPlayer = state.leaderboardSubmitted && entry.score === state.score && entry.name === state.leaderboardName;
      ctx.font = '12px Menlo, monospace';
      ctx.fillStyle = isPlayer ? '#c084fc' : '#aaa';
      ctx.textAlign = 'left';
      ctx.fillText(`#${i + 1}`, x + 24, rowY);
      ctx.fillText(entry.name, x + 80, rowY);
      ctx.textAlign = 'right';
      ctx.fillText(String(entry.score), x + W - 80, rowY);
      ctx.fillText(String(entry.wave), x + W - 24, rowY);
      rowY += 24;
    }
  }

  rowY += 8;

  // Name input (if qualifies and not yet submitted)
  if (hasNameInput) {
    ctx.fillStyle = '#888';
    ctx.font = '11px Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('you made the top 5! enter your name:', cssW / 2, rowY);
    rowY += 20;

    // Input box
    const inputW = 200;
    const inputH = 22;
    const inputX = (cssW - inputW) / 2;
    ctx.fillStyle = 'rgba(30, 30, 30, 0.9)';
    ctx.fillRect(inputX, rowY - 14, inputW, inputH);
    ctx.strokeStyle = '#c084fc';
    ctx.lineWidth = 1;
    ctx.strokeRect(inputX, rowY - 14, inputW, inputH);

    // Name text
    ctx.fillStyle = '#eee';
    ctx.font = '13px Menlo, monospace';
    ctx.textAlign = 'left';
    const displayName = state.leaderboardName;
    ctx.fillText(displayName, inputX + 6, rowY);

    // Blinking cursor
    state.leaderboardCursorBlink++;
    if (state.leaderboardCursorBlink % 60 < 35) {
      const cursorX = inputX + 6 + ctx.measureText(displayName).width + 1;
      ctx.fillStyle = '#c084fc';
      ctx.fillRect(cursorX, rowY - 11, 7, 14);
    }

    rowY += 16;
    ctx.fillStyle = '#666';
    ctx.font = '10px Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('press Enter to submit', cssW / 2, rowY);
  } else {
    // [try again] / [exit] options
    const options = ['[try again]', '[exit]'] as const;
    for (let i = 0; i < options.length; i++) {
      ctx.fillStyle = i === state.deathDialogSelected ? '#c084fc' : '#888';
      ctx.font = '14px Menlo, monospace';
      ctx.textAlign = 'center';
      ctx.fillText(options[i], cssW / 2, rowY + i * 32);
    }
  }
}

// Phase 42 — tier threshold lookup. Iterate descending so the first match wins.
function getTierForWave(wave: number): GameTier {
  if (wave >= 10) return 5;
  if (wave >= 7) return 4;
  if (wave >= 5) return 3;
  if (wave >= 3) return 2;
  return 1;
}

// Phase 42 — HUD chrome (score top-left, pixel hearts top-right).
// Word hint removed in hh7 — replaced by edge incoming indicators.
// Drawn AFTER enemies/particles, BEFORE dialog overlays. All coords are CSS px;
// caller must invoke inside the ctx.scale(dpr, dpr) block.
function drawHUD(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  state: GameState
): void {
  ctx.save();

  // Score — top-left
  ctx.fillStyle = '#e0e0e0';
  ctx.font = ENEMY_FONT;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(`score: ${state.score}`, 16, 16);

  // Pixel hearts — top-right, drawn right-to-left, 8x8 squares with 4px gap
  const HEART_SIZE = 8;
  const HEART_GAP = 4;
  const HEART_FULL = '#ef4444';
  const HEART_EMPTY = 'rgba(239,68,68,0.2)';
  for (let i = 0; i < MAX_LIVES; i++) {
    // Right margin is 48px to clear the DOM ✕ stop button at top-right (right: 12px, ~24px wide)
    const x = cssW - 48 - (HEART_SIZE + HEART_GAP) * i - HEART_SIZE;
    const y = 16;
    ctx.fillStyle = i < state.lives ? HEART_FULL : HEART_EMPTY;
    ctx.fillRect(x, y, HEART_SIZE, HEART_SIZE);
  }

  ctx.restore();
}

// Phase 42 — wave announcement fade-in/hold/fade-out using performance.now() timing.
// 300ms fade-in, 1000ms hold, 300ms fade-out = 1600ms total.
// Pure render — no side effects. Expiry is handled exclusively in the update loop (WR-03).
function drawWaveAnnouncement(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  ann: { text: string; startTime: number }
): void {
  const FADE_IN = 300;
  const HOLD = 1000;
  const FADE_OUT = 300;
  const TOTAL = FADE_IN + HOLD + FADE_OUT;
  const elapsed = performance.now() - ann.startTime;
  if (elapsed >= TOTAL) return;

  let alpha: number;
  if (elapsed < FADE_IN) {
    alpha = 0.5 + (elapsed / FADE_IN) * 0.5; // start at 0.5 so it's immediately visible
  } else if (elapsed < FADE_IN + HOLD) {
    alpha = 1;
  } else {
    alpha = 1 - (elapsed - FADE_IN - HOLD) / FADE_OUT;
  }

  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.fillStyle = '#ffffff';
  ctx.font = '24px "Courier New", monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ann.text, cssW / 2, 60);
  ctx.globalAlpha = 1;
  ctx.restore();
}

// Phase 45 — tier escalation is currently speed-only (wave count drives computeSpeed in enemies.ts).
// Visual tier effects (ghost words, scanlines, pulse, trails, tint) removed per user request.
// TODO: future tier differentiation via enemy spawn PATTERNS (e.g. swarm bursts at tier 3,
//   diagonal approach at tier 4, spiral at tier 5) — add here when ready.

export const GameCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameStateRef = useRef<GameState | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const currentTierRef = useRef<GameTier>(1);
  const frameCountRef = useRef(0);
  const { isActive, deactivate, registerExitHandler, setTier } = useGameContext();

  // SAFE-03 — touch device guard; computed at render time and used to short-circuit effects + render.
  const isTouchDevice =
    typeof window !== 'undefined' && window.matchMedia(TOUCH_MEDIA_QUERY).matches;

  // DPR-aware canvas sizing (Phase 38 — unchanged).
  // Runs on mount and on resize. No drawing happens here;
  // this ensures future rendering phases inherit a crisp buffer on retina displays.
  useEffect(() => {
    if (isTouchDevice) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const syncSize = (): void => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
    };

    syncSize();
    window.addEventListener('resize', syncSize);
    return () => {
      window.removeEventListener('resize', syncSize);
    };
  }, [isTouchDevice]);

  // Game loop + Phase 39 listener lifecycle.
  // Double-mount guard via gameStateRef.current check (StrictMode resilience).
  useEffect(() => {
    if (!isActive || isTouchDevice) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (gameStateRef.current) return; // StrictMode double-mount guard

    gameStateRef.current = createInitialState();
    gameStateRef.current.wordPool = extractWordPool();
    initCharacter();

    // Phase 44 — random pool selection for dialogue reactions
    const randomFrom = (pool: readonly string[]): string =>
      pool[Math.floor(Math.random() * pool.length)];

    // Phase 44 — intro sequence (fires once; skipped on [try again] because hasPlayedIntro stays true)
    if (!gameStateRef.current.hasPlayedIntro) {
      INTRO_LINES.forEach(line => queueLine(line));
      gameStateRef.current.hasPlayedIntro = true;
    }

    setMuted(false); // Always start unmuted — don't let a stale sessionStorage mute new sessions
    playSound('gameStart');
    // Reset opacity in case a previous cycle left it faded
    canvas.style.opacity = '1';

    // Get canvas context once for use in handleTypedKey
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleMouseMove = (e: MouseEvent): void => {
      const state = gameStateRef.current;
      if (!state) return;
      state.mouseX = e.clientX;
      state.mouseY = e.clientY;
      state.idleTimer = 0;
      state.lastMouseMoveFrame = frameCountRef.current;
    };

    // Internal exitGame — the ONLY path that starts the fade.
    // Shared between Escape -> [exit], death -> [exit], and DOM stop button.
    const exitGame = (): void => {
      const state = gameStateRef.current;
      if (!state || state.exiting) return; // Pitfall 2 guard
      // Phase 44 — exit farewell + wave animation (must come BEFORE resetDialogue)
      EXIT_LINES.forEach(line => queueLine(line));
      setCharacterState('wave');
      state.paused = false;
      state.dead = false;
      state.exiting = true;
      state.exitStartTime = performance.now();
      playSound('exitSound');
      // resetDialogue removed — cleanup handles it; exit lines display during 800ms fade
    };

    // [try again] — in-place reset WITHOUT deactivate/activate cycle (Pitfall 5).
    // hasPlayedIntro stays true so Phase 44 intro does not replay.
    const resetGameInPlace = (): void => {
      const state = gameStateRef.current;
      if (!state) return;
      state.paused = false;
      state.dead = false;
      state.exitDialogSelected = 0;
      state.deathDialogSelected = 0;
      // Phase 41: zero enemy/particle/wave/score state and refill pool
      state.enemies.length = 0;
      state.particles.length = 0;
      state.wordsCleared = 0;
      state.wave = 1;
      state.score = 0;
      state.incomingFrames = 0;
      // Phase 42 — reset HUD/lives/wave announcement
      state.lives = MAX_LIVES;
      state.waveAnnouncement = null;
      state.incomingIndicators.length = 0;
      state.lastAnnouncedWave = 1;
      // Phase 44 — reset survival flag so long-survival line can fire again on new run
      state.hasPlayedSurvival = false;
      // Phase 47 — reset quip timers and cooldowns for new run
      state.idleTimer = 0;
      state.movingTimer = 0;
      state.lastTypedFrame = 0;
      state.lastMouseMoveFrame = 0;
      state.quipCooldowns.clear();
      state.lastTierForQuip = 0;
      // Phase 48 — reset safe zone for new run
      state.clearAllPhase = 'idle';
      state.clearAllTimer = 0;
      state.safeZoneUses = 0;
      state.safeZoneEntered = false;
      resetSafeZone();
      setSafeZoneLevel(0);
      // Phase 49 — reset selection walls for new run
      state.selectionRects = [];
      state.selectionDirty = false;
      state.hasDiscoveredSelection = false;
      resetSelectionWalls();
      // Phase 50 -- reset streak for new run
      state.streakSpawnTimer = 0;
      state.trailHitCooldown = 0;
      resetStreak();
      // Phase 51 -- reset bomb for new run
      state.bombSpawnTimer = 0;
      resetBomb();
      // Leaderboard reset
      state.leaderboardName = '';
      state.leaderboardQualifies = false;
      state.leaderboardSubmitted = false;
      state.leaderboardCursorBlink = 0;
      // Refill pool so [try again] has words immediately
      restoreAll();
      state.wordPool = extractWordPool();
      resetDialogue();
    };

    // Trigger death + fetch leaderboard
    const triggerDeath = (state: GameState): void => {
      state.dead = true;
      state.deathDialogSelected = 0;
      state.leaderboardLoading = true;
      state.leaderboardName = '';
      state.leaderboardSubmitted = false;
      state.leaderboardCursorBlink = 0;
      // Update personal best
      if (state.score > getPersonalBest()) {
        setPersonalBest(state.score);
      }
      // Fetch leaderboard async
      fetchLeaderboard().then(board => {
        const s = gameStateRef.current;
        if (!s || !s.dead) return;
        s.leaderboard = board;
        s.leaderboardLoading = false;
        s.leaderboardQualifies = scoreQualifies(s.score, board);
      });
    };

    // Phase 41 — typing handler: routes single-character keys to enemy matching.
    // Returns true if a match was found (used by mute toggle fallback in handleKeyDown).
    const handleTypedKey = (key: string): boolean => {
      const state = gameStateRef.current;
      if (!state) return false;
      const lower = key.toLowerCase();
      for (let idx = 0; idx < state.enemies.length; idx++) {
        const enemy = state.enemies[idx];
        const nextChar = enemy.word[enemy.typed];
        if (!nextChar) continue;
        if (nextChar.toLowerCase() !== lower) continue;

        // Compute exact glyph center x in canvas CSS px
        ctx.save();
        ctx.font = ENEMY_FONT;
        const { pillW } = measurePill(ctx, enemy.word, enemy.typed);
        const pillX = enemy.x - pillW / 2;
        const textStartX = pillX + PILL_PAD_X;
        // Next char is always the first visible character — offset = 0
        const charW = ctx.measureText(nextChar).width;
        ctx.restore();
        const particleX = textStartX + charW / 2;

        spawnParticle(nextChar, particleX, enemy.y, state.particles, undefined, undefined, enemy.fontSize);
        playSound('letterDestroy');
        enemy.typed++;
        state.lastTypedFrame = frameCountRef.current;

        if (enemy.typed >= enemy.word.length) {
          deactivateWord(enemy.word);
          state.enemies.splice(idx, 1);
          state.wordsCleared++;
          const prevWave = state.wave;
          state.wave = Math.floor(state.wordsCleared / 5) + 1;
          state.score += 10;
          playSound('wordCleared');
          // Phase 47 — contextual word-cleared quip: only when dialogue is idle so reaction is immediate
          if (currentTierRef.current < 5 && !isDialogueActive() && Math.random() < 0.5) {
            queueLine(getWordClearedQuip(enemy.word));
          }
          setCharacterState('react-clear');
          state.incomingFrames = 55;
          // Phase 47 — wave-clear quip fires when wave advances
          if (state.wave > prevWave) {
            onWaveClear(state);
          }
        }
        return true; // only first matching enemy
      }
      // No match -> ENEMY-05: no-op
      return false;
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
      // Unlock AudioContext on first gesture — Chrome 71+ suspends contexts
      // created outside a user gesture; resume() must be called FROM a gesture.
      ensureAudioUnlocked();

      const state = gameStateRef.current;
      if (!state || state.exiting) return;

      // Phase 43 — dialogue advance: Space/Enter skip-to-end or advance queue
      if ((e.key === ' ' || e.key === 'Enter') && !state.paused && !state.dead && !state.exiting && isDialogueActive()) {
        e.preventDefault();
        advanceDialogue();
        return;
      }

      // Phase 41 — typing routes to enemies during normal gameplay.
      // Phase 43 intentional design: typed characters are NOT blocked while
      // dialogue is active. Dialogue is non-interruptive — enemies keep moving
      // and can be destroyed by typing during cutscenes. If that design ever
      // changes, add `if (isDialogueActive()) return;` before handleTypedKey.
      if (!state.paused && !state.dead && !state.exiting) {
        if (
          e.key.length === 1 &&
          !e.ctrlKey &&
          !e.metaKey &&
          !e.altKey &&
          e.key !== 'Escape'
        ) {
          e.preventDefault();
          handleTypedKey(e.key);
          return;
        }
      }

      // Escape during gameplay — open exit dialog (Pitfall 4: only when not already paused/dead)
      if (e.key === 'Escape') {
        if (state.dead) return; // death dialog is modal — Escape does nothing
        if (!state.paused) {
          e.preventDefault();
          state.paused = true;
          state.exitDialogSelected = 0;
          queueLine(randomFrom(PAUSE_QUIPS));
          return;
        }
        // Escape while paused -> cancel dialog, resume
        e.preventDefault();
        state.paused = false;
        return;
      }

      // Arrow keys + Enter only meaningful while a dialog is open
      if (state.paused) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          state.exitDialogSelected = ((state.exitDialogSelected + 2) % 3) as 0 | 1 | 2;
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          state.exitDialogSelected = ((state.exitDialogSelected + 1) % 3) as 0 | 1 | 2;
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (state.exitDialogSelected === 0) {
            state.paused = false; // [keep playing]
          } else if (state.exitDialogSelected === 2) {
            setMuted(!isMuted()); // [mute] / [unmute] — stay in dialog so user can see change
          } else {
            exitGame(); // [exit]
          }
          return;
        }
      }

      if (state.dead) {
        const isNameEntry = state.leaderboardQualifies && !state.leaderboardSubmitted;

        if (isNameEntry) {
          e.preventDefault();
          // Name input mode
          if (e.key === 'Backspace') {
            state.leaderboardName = state.leaderboardName.slice(0, -1);
            return;
          }
          if (e.key === 'Escape') {
            // Skip name entry, go to options
            state.leaderboardQualifies = false;
            return;
          }
          if (e.key === 'Enter') {
            const trimmed = state.leaderboardName.trim();
            if (trimmed.length > 0) {
              state.leaderboardSubmitted = true;
              submitScore(trimmed, state.score, state.wave, state.wordsCleared).then(board => {
                const s = gameStateRef.current;
                if (s) s.leaderboard = board;
              });
            }
            return;
          }
          // Accept alphanumeric + space + dash + underscore
          if (e.key.length === 1 && /^[a-zA-Z0-9 _-]$/.test(e.key) && state.leaderboardName.length < LEADERBOARD_MAX_NAME) {
            state.leaderboardName += e.key;
          }
          return;
        }

        // Normal death dialog navigation (after submission or non-qualifying)
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          state.deathDialogSelected = 0;
          return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          state.deathDialogSelected = 1;
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (state.deathDialogSelected === 0) {
            resetGameInPlace(); // [try again]
          } else {
            exitGame(); // [exit]
          }
          return;
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', handleKeyDown);

    // Phase 49 -- selection walls: detect text selection changes
    const onSelectionChange = (): void => {
      const s = gameStateRef.current;
      if (!s) return;
      handleSelectionChange(s, canvas);
    };
    document.addEventListener('selectionchange', onSelectionChange);

    // Expose openExitDialog to context so Home.tsx DOM stop button opens the same
    // canvas exit dialog as pressing Escape (sets paused = true).
    const openExitDialog = (): void => {
      const s = gameStateRef.current;
      if (!s || s.dead || s.exiting || s.paused) return;
      s.paused = true;
      s.exitDialogSelected = 0;
    };
    registerExitHandler(openExitDialog);

    const stopLoop = startGameLoop(canvas, {
      update: (dt: number): void => {
        const state = gameStateRef.current;
        if (!state) return;
        if (state.paused || state.dead || state.exiting) return;

        frameCountRef.current++;
        const scale = dt * 60;
        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.width / dpr;
        const cssH = canvas.height / dpr;

        // Move enemies (Phase 48: freeze during clear-all sequence)
        if (state.clearAllPhase === 'idle') {
          for (const enemy of state.enemies) {
            if (enemy.type === 'streak') {
              updateStreak(enemy, dt);
            } else if (enemy.type === 'bomb') {
              updateBomb(enemy, state.mouseX, state.mouseY, dt);
            } else {
              updateEnemy(enemy, state.mouseX, state.mouseY, state.wave, dt);
            }
          }
        }

        // Phase 42 — enemy/cursor collision: lose a life and remove the enemy
        // Phase 48: skip collision during clear-all fighting/returning
        // Phase 49: skip collision when cursor is inside a text selection zone (invincibility)
        if (state.clearAllPhase === 'idle' && !isInsideSelection(state)) {
          const HIT_RADIUS = 60;
          for (let i = state.enemies.length - 1; i >= 0; i--) {
            const enemy = state.enemies[i];
            const dist = Math.hypot(enemy.x - state.mouseX, enemy.y - state.mouseY);
            if (dist < HIT_RADIUS) {
              deactivateWord(enemy.word);
              state.enemies.splice(i, 1);
              state.lives = Math.max(0, state.lives - 1);
              if (state.lives === 0) {
                triggerDeath(state);
                queueLine(randomFrom(DEATH_POOL));
              }
              playSound('playerHit');
              // Phase 44 — player-hit reaction + character recoil
              if (!state.dead) {
                if (currentTierRef.current < 5) {
                  queueLine(randomFrom(PLAYER_HIT_POOL));
                }
                setCharacterState('react-hit');
              }
            }
          }

          // Phase 51 -- bomb detonation check
          for (let i = state.enemies.length - 1; i >= 0; i--) {
            const enemy = state.enemies[i];
            if (enemy.type !== 'bomb') continue;
            if ((enemy.fuseTimer ?? 1) > 0) continue;
            // Skip explosion if fully typed (edge case: typed on same frame fuse expires)
            if (enemy.typed >= enemy.word.length) {
              deactivateWord(enemy.word);
              state.enemies.splice(i, 1);
              continue;
            }
            // Detonate
            const blastRadius = getBombBlastRadius(enemy);
            const dist = Math.hypot(enemy.x - state.mouseX, enemy.y - state.mouseY);
            triggerBombDetonation(enemy, state.particles);
            if (dist < blastRadius) {
              state.lives = Math.max(0, state.lives - 1);
              playSound('playerHit');
              if (state.lives === 0) {
                triggerDeath(state);
                queueLine(randomFrom(DEATH_POOL));
              } else {
                if (currentTierRef.current < 5) {
                  queueLine(randomFrom(PLAYER_HIT_POOL));
                }
                setCharacterState('react-hit');
              }
            }
            deactivateWord(enemy.word);
            state.enemies.splice(i, 1);
          }

          // Phase 50 -- streak trail damage with hit-invincibility cooldown (~1.5s / 90 frames)
          if (state.trailHitCooldown > 0) {
            state.trailHitCooldown -= dt * 60;
          } else if (checkTrailCollision(state)) {
            state.trailHitCooldown = 90;
            state.lives = Math.max(0, state.lives - 1);
            if (state.lives === 0) {
              triggerDeath(state);
              queueLine(randomFrom(DEATH_POOL));
            }
            playSound('playerHit');
            if (!state.dead) {
              if (currentTierRef.current < 5) {
                queueLine(randomFrom(PLAYER_HIT_POOL));
              }
              setCharacterState('react-hit');
            }
          }
        }

        // Update particles
        updateParticles(state.particles, dt);

        // Phase 48 — safe zone: check entry edge-trigger, advance clear-all state machine
        checkSafeZoneEntry(state, cssW, cssH);
        tickSafeZone(state, dt);

        // Phase 49 -- selection walls: auto-destroy overlapping enemies, check discovery hint
        tickSelectionWalls(state);
        checkSelectionDiscovery(state);

        // Phase 50 -- tick streak warnings (convert expired warnings to enemies)
        tickStreakWarnings(state, dt);

        // Phase 50 -- remove off-canvas streak enemies (no life penalty - STREAK-05)
        for (let i = state.enemies.length - 1; i >= 0; i--) {
          const enemy = state.enemies[i];
          if (enemy.type === 'streak' && isStreakOffCanvas(enemy, cssW, cssH)) {
            deactivateWord(enemy.word);
            state.enemies.splice(i, 1);
          }
        }

        // Incoming countdown
        if (state.incomingFrames > 0) {
          state.incomingFrames -= scale;
          if (state.incomingFrames < 0) state.incomingFrames = 0;
        }

        // hh7 — tick down existing indicators and convert expired ones to enemies
        for (let i = state.incomingIndicators.length - 1; i >= 0; i--) {
          const ind = state.incomingIndicators[i];
          ind.timer -= scale;
          ind.pulsePhase += 0.12 * scale;
          if (ind.timer <= 0) {
            state.enemies.push(spawnEnemyAt(ind.entry, ind.spawnX, ind.spawnY));
            state.incomingIndicators.splice(i, 1);
          }
        }

        // Phase 44 — drive character talk/idle from dialogue activity (transition only)
        const dialogueActive = isDialogueActive();
        const charState = getCharacterState();
        if (dialogueActive && charState !== 'talk' && charState !== 'react-hit' && charState !== 'react-clear') {
          setCharacterState('talk');
        } else if (!dialogueActive && charState === 'talk') {
          setCharacterState('idle');
        }

        // hh7 — queue new indicators (max 3 total active+incoming, gated by incomingFrames gap)
        // Phase 44 — also block spawning while intro dialogue is still playing
        //   (intro = dialogue active + no enemies exist yet)
        const introStillPlaying = isDialogueActive() &&
          state.enemies.length === 0 && state.incomingIndicators.length === 0;
        const totalActive = state.enemies.length + state.incomingIndicators.length + getPendingWarningCount();
        if (totalActive < 3 && state.incomingFrames <= 0 && !introStillPlaying) {
          // Refill pool if exhausted
          if (state.wordPool.length === 0) {
            restoreAll();
            state.wordPool = extractWordPool();
          }
          while (state.wordPool.length > 0) {
            const entry = state.wordPool.shift();
            if (!entry) break;
            const rect = activateWord(entry);
            if (rect !== null || entry.category === 'fallback') {
              const spawn = getSpawnPosition(cssW, cssH);
              const indicator: IncomingIndicator = {
                word: entry.word,
                entry,
                edge: spawn.edge,
                edgeX: spawn.edgeX,
                edgeY: spawn.edgeY,
                spawnX: spawn.x,
                spawnY: spawn.y,
                timer: 80,
                pulsePhase: 0,
              };
              state.incomingIndicators.push(indicator);
              state.incomingFrames = 55;
              break;
            }
          }
        }

        // Phase 50 -- streak enemy spawn (independent of homing, wave gate STREAK-06)
        if (state.wave >= 3 && state.clearAllPhase === 'idle') {
          if (state.streakSpawnTimer <= 0) {
            const streakTotal = state.enemies.filter(e => e.type === 'streak').length + getPendingWarningCount();
            if (streakTotal < 2) {
              spawnStreakWarning(state, cssW, cssH);
            }
            state.streakSpawnTimer = 360; // ~6s at 60fps between streak spawns -- reset regardless of cap
          } else {
            state.streakSpawnTimer -= dt * 60;
          }
        }

        // Phase 51 -- bomb enemy spawn (wave 5+ gate, max 1 concurrent)
        if (state.wave >= 5 && state.clearAllPhase === 'idle') {
          if (state.bombSpawnTimer <= 0) {
            const bombCount = state.enemies.filter(e => e.type === 'bomb').length;
            if (bombCount < 1) {
              spawnBomb(state, cssW, cssH);
            }
            state.bombSpawnTimer = 480; // ~8s at 60fps between bomb spawn attempts
          } else {
            state.bombSpawnTimer -= dt * 60;
          }
        }

        // Phase 51 -- tick blast flash animation
        tickBlastFlash(dt);

        // Phase 42 — trigger wave announcement when wave advances past lastAnnouncedWave.
        // lastAnnouncedWave is initialized to 1 so wave 1 (the opening state) is NOT announced.
        if (state.wave > state.lastAnnouncedWave) {
          state.waveAnnouncement = { text: `WAVE ${state.wave}`, startTime: performance.now() };
          state.lastAnnouncedWave = state.wave;
        }

        // Phase 44 — long survival line (fires once at wave 10+)
        if (state.wave >= 10 && !state.hasPlayedSurvival) {
          state.hasPlayedSurvival = true;
          queueLine(LONG_SURVIVAL_LINE);
        }

        // Phase 42 — clear expired wave announcement (1600ms total = 300+1000+300)
        if (state.waveAnnouncement) {
          const elapsedAnn = performance.now() - state.waveAnnouncement.startTime;
          if (elapsedAnn >= 1600) {
            state.waveAnnouncement = null;
          }
        }

        // Phase 42 — tier progression. Gated by ref comparison to avoid 60fps React re-renders.
        const newTier = getTierForWave(state.wave);
        if (newTier !== currentTierRef.current) {
          currentTierRef.current = newTier;
          setTier(newTier);
          // Phase 44 — tier transition dialogue (SCRIPT-03: tier 5 gets NO dialogue)
          if ((newTier as number) in TIER_LINES) {
            queueLine(TIER_LINES[newTier as 2 | 3 | 4]);
          }
          // Phase 44 — panic state at tier 3+ (CHAR-02)
          if (newTier >= 3) {
            setCharacterState('panic');
          }
        }

        // Phase 47 — character personality quip system
        tickQuips(state, currentTierRef.current, frameCountRef.current);

        // Phase 43 — dialogue typewriter advance
        const typedNewChar = updateDialogue(dt);
        if (typedNewChar) {
          playSound('dialogueTick');
        }

        // Phase 44 — character animation update (uses typedNewChar for talk state)
        updateCharacter(dt, typedNewChar);
      },
      render: (ctx: CanvasRenderingContext2D): void => {
        const state = gameStateRef.current;
        if (!state) return;

        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.width / dpr;
        const cssH = canvas.height / dpr;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.scale(dpr, dpr);

        // Phase 41 — draw particles beneath enemies
        drawParticles(ctx, state.particles);

        // Phase 51 -- blast flash effect (behind enemies)
        drawBlastFlash(ctx);

        // Phase 50 -- draw streak trails (before pills so pills render on top)
        for (const enemy of state.enemies) {
          if (enemy.type === 'streak') {
            drawStreakTrail(ctx, enemy);
          }
        }

        for (const enemy of state.enemies) {
          if (enemy.type === 'streak') {
            drawStreakEnemy(ctx, enemy);
          } else if (enemy.type === 'bomb') {
            drawBombEnemy(ctx, enemy);
          } else {
            drawEnemy(ctx, enemy);
          }
        }

        // hh7 — draw incoming edge indicators above enemies, before HUD
        for (const ind of state.incomingIndicators) {
          drawIncomingIndicator(ctx, ind);
        }

        // Phase 50 -- draw streak warnings (pulsing chevrons at canvas edges)
        drawAllStreakWarnings(ctx);

        // Phase 44 — pixel character (above particles/enemies, below HUD)
        drawCharacter(ctx, cssW, cssH);

        // Phase 48 — safe zone (not rendered while paused/dead/exiting)
        if (!state.paused && !state.dead && !state.exiting) {
          drawSafeZone(ctx, cssW, cssH, state.safeZoneUses, state.clearAllPhase);
          // Phase 49 -- selection wall overlay
          drawSelectionOverlay(ctx, state.selectionRects);
        }

        // Phase 42 — HUD chrome (score, hearts) drawn above game content, below dialog overlays
        drawHUD(ctx, cssW, cssH, state);
        if (state.waveAnnouncement) {
          drawWaveAnnouncement(ctx, cssW, state.waveAnnouncement);
        }

        // Phase 43 — dialogue box (drawn above HUD, below exit/death dialogs)
        drawDialogue(ctx, cssW, cssH);

        if (state.paused) {
          drawExitDialog(ctx, cssW, cssH, state.exitDialogSelected, 'exit game?', [
            '[keep playing]',
            '[exit]',
            isMuted() ? '[unmute]' : '[mute]',
          ]);
        }
        if (state.dead) {
          drawDeathDialog(ctx, cssW, cssH, state);
        }

        ctx.restore();

        // 800ms fade is driven via direct style.opacity mutation (not React state)
        if (state.exiting && canvasRef.current) {
          const elapsed = performance.now() - state.exitStartTime;
          const progress = Math.min(elapsed / EXIT_FADE_MS, 1);
          canvasRef.current.style.opacity = String(1 - progress);
          if (progress >= 1) {
            // Full cleanup + deactivate — cleanupRef runs the listener/loop teardown,
            // deactivate() triggers React to re-render and the isActive useEffect
            // will clear cleanupRef on its own. We guard against double-run with
            // a local flag: nulling gameStateRef in cleanup prevents re-entry.
            const finish = cleanupRef.current;
            cleanupRef.current = null;
            finish?.();
            deactivate();
          }
        }
      },
    });

    cleanupRef.current = (): void => {
      stopLoop();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectionchange', onSelectionChange);
      registerExitHandler(null);
      // Phase 41: restore any DOM words still hidden before nulling state
      restoreAll();
      resetDialogue();
      resetSafeZone();
      cleanupSelectionWalls();
      resetStreak();
      resetBomb();
      gameStateRef.current = null;
      // Reset canvas opacity so next activation cycle starts clean
      if (canvasRef.current) {
        canvasRef.current.style.opacity = '0';
      }
    };

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [isActive, isTouchDevice, deactivate, registerExitHandler, setTier]);

  if (isTouchDevice) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        ...CANVAS_BASE_STYLE,
        opacity: isActive ? 1 : 0,
        zIndex: isActive ? 5000 : 0,
      }}
    />
  );
};
